/**
 * The post-donation share card.
 *
 * After a gift, the donor is offered the one thing that makes the next one
 * easier: a link they can send. The link is the campaign's or the page's own
 * **share** URL - the server-rendered one with the preview tags - because
 * WhatsApp is where a donation link actually travels, and a preview is what
 * makes somebody open it.
 *
 * The card shows the real numbers the API returned, and it never invents a
 * target: a share link to a campaign that does not exist is not a share link.
 */
import { useTranslation } from 'react-i18next';

export interface ShareCardProps {
  /** The shareable URL: the server-rendered page with the preview tags. */
  shareUrl: string;
  /** The title of the campaign or page being shared. */
  title: string;
  /** What has been raised so far, in cents. Null when nothing has come in. */
  raisedCents: number | null;
  /** The goal, in cents. */
  goalCents: number;
  /** The web origin, so the links can be built. */
  webOrigin: string;
}

/** The WhatsApp share link: the channel the link travels on. */
function whatsappLink(shareUrl: string, title: string): string {
  return `https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`;
}

/** The X (Twitter) share link. */
function xLink(shareUrl: string): string {
  return `https://x.com/intent/post?url=${encodeURIComponent(shareUrl)}`;
}

export function ShareCard({ shareUrl, title, raisedCents, goalCents, webOrigin }: ShareCardProps) {
  const { t } = useTranslation();
  const raised = raisedCents === null ? 0 : raisedCents;
  const percent = goalCents > 0 ? Math.min(100, Math.round((raised / goalCents) * 100)) : 0;

  return (
    <section data-testid="share-card">
      <h3>{t('share.title')}</h3>
      <p data-testid="share-progress">
        {t('share.raised', { amount: (raised / 100).toFixed(2) })} ({percent}%)
      </p>
      <a
        href={whatsappLink(shareUrl, title)}
        data-testid="share-whatsapp"
        target="_blank"
        rel="noreferrer"
      >
        {t('share.whatsapp')}
      </a>
      <a href={xLink(shareUrl)} data-testid="share-x" target="_blank" rel="noreferrer">
        {t('share.x')}
      </a>
      <a href={shareUrl} data-testid="share-link">
        {shareUrl.replace(webOrigin, '')}
      </a>
    </section>
  );
}
